/**
 * Shared deck utility functions to reduce code duplication
 */

/**
 * Convert deck object to the structured format used in TCG matches
 * @param {Object} deck - Deck object from deck context
 * @returns {Object} Structured deck format for TCG matches
 */
export const deckToStructuredFormat = (deck) => {
  if (!deck) return null;
  
  return {
    _id: deck._id,
    name: deck.name,
    description: deck.description || '',
    pokemon: deck.pokemon || [],
    trainers: deck.trainers || [],
    energy: deck.energy || [],
    pokemonSprite: deck.pokemonSprite || null,
    secondaryPokemonSprite: deck.secondaryPokemonSprite || null,
    category: deck.category || 'Uncategorized',
    coreCards: deck.coreCards || [],
    isVGCTeam: deck.isVGCTeam || false
  };
};

/**
 * Convert legacy activeDeck array format to structured format
 * @param {Array} activeDeckArray - Legacy array format [name, ...cardNames]
 * @param {Array} decks - Available decks to match against
 * @returns {Object|null} Structured deck format or null
 */
export const legacyArrayToStructuredDeck = (activeDeckArray, decks = []) => {
  if (!Array.isArray(activeDeckArray) || activeDeckArray.length === 0) {
    return null;
  }
  
  const deckName = activeDeckArray[0];
  const matchingDeck = decks.find(deck => deck.name === deckName);
  
  if (matchingDeck) {
    return deckToStructuredFormat(matchingDeck);
  }
  
  // If no matching deck found, create a basic structure
  return {
    _id: null,
    name: deckName,
    description: '',
    pokemon: [],
    trainers: [],
    energy: [],
    pokemonSprite: null,
    secondaryPokemonSprite: null,
    category: 'Legacy Deck',
    coreCards: [],
    isVGCTeam: false
  };
};

/**
 * Extract deck name from various deck formats
 * @param {Object|Array|string} deckData - Deck in various formats
 * @returns {string} Deck name
 */
export const getDeckName = (deckData) => {
  if (typeof deckData === 'string') {
    return deckData;
  }
  
  if (Array.isArray(deckData) && deckData.length > 0) {
    return deckData[0];
  }
  
  if (deckData && typeof deckData === 'object') {
    return deckData.name || '';
  }
  
  return '';
};

/**
 * Calculate total cards in a deck
 * @param {Object} deck - Deck object
 * @returns {number} Total card count
 */
export const getTotalCardCount = (deck) => {
  if (!deck) return 0;
  
  const pokemonCount = (deck.pokemon || []).reduce((total, card) => total + (card.count || card.quantity || 1), 0);
  const trainerCount = (deck.trainers || []).reduce((total, card) => total + (card.count || card.quantity || 1), 0);
  const energyCount = (deck.energy || []).reduce((total, card) => total + (card.count || card.quantity || 1), 0);
  
  return pokemonCount + trainerCount + energyCount;
};

/**
 * Check if deck is complete (60 cards for TCG)
 * @param {Object} deck - Deck object
 * @returns {boolean} Whether deck is complete
 */
export const isDeckComplete = (deck) => {
  if (!deck) return false;
  
  if (deck.isVGCTeam) {
    // VGC teams should have 6 Pokemon
    return (deck.pokemon || []).length === 6;
  }
  
  // TCG decks should have 60 cards
  return getTotalCardCount(deck) === 60;
};

/**
 * Get primary Pokemon sprite from deck
 * @param {Object} deck - Deck object
 * @returns {Object|null} Sprite object or null
 */
export const getDeckPrimarySprite = (deck) => {
  return deck?.pokemonSprite || null;
};

/**
 * Get secondary Pokemon sprite from deck
 * @param {Object} deck - Deck object
 * @returns {Object|null} Sprite object or null
 */
export const getDeckSecondarySprite = (deck) => {
  return deck?.secondaryPokemonSprite || null;
};

/**
 * Validate deck for match logging
 * @param {Object} deck - Deck object
 * @returns {Object} Validation result
 */
export const validateDeckForMatch = (deck) => {
  if (!deck) {
    return {
      isValid: false,
      message: 'No deck selected'
    };
  }
  
  if (!deck.name) {
    return {
      isValid: false,
      message: 'Deck must have a name'
    };
  }
  
  if (deck.name.includes('(Incomplete)')) {
    return {
      isValid: false,
      message: 'Cannot use incomplete deck for matches'
    };
  }
  
  if (!isDeckComplete(deck)) {
    const expectedCount = deck.isVGCTeam ? 6 : 60;
    const actualCount = deck.isVGCTeam ? (deck.pokemon || []).length : getTotalCardCount(deck);
    
    return {
      isValid: false,
      message: `Deck must have ${expectedCount} ${deck.isVGCTeam ? 'Pokemon' : 'cards'} (currently has ${actualCount})`
    };
  }
  
  return {
    isValid: true,
    message: 'Deck is valid'
  };
};

/**
 * Create a deck card list for display
 * @param {Object} deck - Deck object
 * @returns {Array} Array of card entries for display
 */
export const createDeckCardList = (deck) => {
  if (!deck) return [];
  
  const cardList = [];
  
  // Add Pokemon cards
  if (deck.pokemon && deck.pokemon.length > 0) {
    cardList.push({ type: 'header', name: 'Pokemon' });
    deck.pokemon.forEach(card => {
      cardList.push({
        type: 'card',
        name: card.name,
        count: card.count || card.quantity || 1,
        supertype: 'Pokemon'
      });
    });
  }
  
  // Add Trainer cards
  if (deck.trainers && deck.trainers.length > 0) {
    cardList.push({ type: 'header', name: 'Trainers' });
    deck.trainers.forEach(card => {
      cardList.push({
        type: 'card',
        name: card.name,
        count: card.count || card.quantity || 1,
        supertype: 'Trainer'
      });
    });
  }
  
  // Add Energy cards
  if (deck.energy && deck.energy.length > 0) {
    cardList.push({ type: 'header', name: 'Energy' });
    deck.energy.forEach(card => {
      cardList.push({
        type: 'card',
        name: card.name,
        count: card.count || card.quantity || 1,
        supertype: 'Energy'
      });
    });
  }
  
  return cardList;
};

/**
 * Compare two decks for similarity
 * @param {Object} deck1 - First deck
 * @param {Object} deck2 - Second deck
 * @returns {number} Similarity score (0-1)
 */
export const compareDeckSimilarity = (deck1, deck2) => {
  if (!deck1 || !deck2) return 0;
  
  const getDeckCardSet = (deck) => {
    const cards = new Set();
    ['pokemon', 'trainers', 'energy'].forEach(type => {
      (deck[type] || []).forEach(card => {
        cards.add(card.name.toLowerCase());
      });
    });
    return cards;
  };
  
  const cards1 = getDeckCardSet(deck1);
  const cards2 = getDeckCardSet(deck2);
  
  const intersection = new Set([...cards1].filter(card => cards2.has(card)));
  const union = new Set([...cards1, ...cards2]);
  
  return union.size > 0 ? intersection.size / union.size : 0;
};